import { useNavigate, useParams, useSearch, useLocation } from '@tanstack/react-router'
import { useEffect, useMemo, useRef } from 'react'
import { router as tsRouter } from '../../router'

type RouterEventCallback = (url: string) => void

class RouterEventEmitter {
    private listeners: Record<string, Set<RouterEventCallback>> = {}

    on(event: string, cb: RouterEventCallback) {
        if (!this.listeners[event]) this.listeners[event] = new Set()
        this.listeners[event].add(cb)
    }

    off(event: string, cb: RouterEventCallback) {
        this.listeners[event]?.delete(cb)
    }

    emit(event: string, url?: string) {
        this.listeners[event]?.forEach(cb => cb(url ?? ''))
    }
}

export const routerEvents = new RouterEventEmitter()

// Helper to resolve Next.js-style url/as to a string path
const resolveHref = (url: string | { pathname?: string; query?: any }, as?: any) => {
    const dest = as || url
    if (typeof dest === 'string') return dest

    let path = dest.pathname ?? '/'
    if (dest.query) {
        const params = new URLSearchParams()
        Object.entries(dest.query).forEach(([k, v]) => {
            if (Array.isArray(v)) {
                v.forEach(val => params.append(k, String(val)))
            } else if (v !== undefined && v !== null) {
                params.append(k, String(v))
            }
        })
        const qs = params.toString()
        if (qs) path += (path.includes('?') ? '&' : '?') + qs
    }
    return path
}

/**
 * Split a resolved href into { path, search } so that TanStack Router's
 * navigate() always receives an explicit `search` object.
 *
 * When the query string is embedded in `to`, TanStack Router's buildLocation
 * ignores it and defaults search to `(s) => s` — inheriting the current
 * page's search params.  From /quote/list?tab=all this causes the tab param
 * to bleed into the destination URL (e.g. /invoice/create?tab=all).
 *
 * Returning `search: {}` when there is no query string ensures TanStack
 * Router receives an explicit empty object, which REPLACES (not merges)
 * the current search — matching Next.js push/replace semantics.
 */
const splitHref = (href: string): { path: string; search: Record<string, string | string[]> } => {
    const qIdx = href.indexOf('?')
    if (qIdx === -1) return { path: href, search: {} }
    const path = href.slice(0, qIdx)
    const params = new URLSearchParams(href.slice(qIdx + 1))
    const search: Record<string, string | string[]> = {}
    for (const key of new Set(params.keys())) {
        const values = params.getAll(key)
        search[key] = values.length === 1 ? values[0] : values
    }
    return { path, search }
}

export function useRouter() {
    const navigate = useNavigate()
    const params   = useParams({ strict: false })
    const search   = useSearch({ strict: false })
    const location = useLocation()

    // Capture as a primitive string so the stale-guard closure retains the
    // pathname from THIS render even if TanStack Router mutates the location
    // object in-place when a navigation commits during the same React batch.
    const pathnameAtRender = location.pathname

    const prevHrefRef = useRef(location.href)

    // Memoize so the object reference is stable when the URL hasn't changed —
    // prevents useEffect([router.query]) in ScTable from firing infinitely.
    // Include a serialized form of params in deps: TanStack Router can update
    // params in a render where location.pathname has already been updated,
    // so listing only pathname/search misses those param-only re-renders and
    // causes router.query.id to lag by one navigation on same-route transitions.
    const paramStr = Object.entries(params as Record<string, unknown>)
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([k, v]) => `${k}=${v}`)
        .join('&')
    const query = useMemo(
        () => ({ ...params, ...(search as any) } as Record<string, any>),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [location.pathname, location.search, paramStr]
    )

    useEffect(() => {
        const href = location.href
        if (prevHrefRef.current !== href) {
            routerEvents.emit('routeChangeComplete', href)
            routerEvents.emit('hashChangeComplete', href)
            prevHrefRef.current = href
        }
    }, [location.href])

    return {
        // url can be a string or Next.js { pathname, query } object.
        // Always split path from search so TanStack Router can match route
        // segments correctly and so search params are not silently dropped.
        push:     (url: string | { pathname?: string; query?: any }, as?: any, _opts?: any) => {
                      const { path, search } = splitHref(resolveHref(url, as))
                      return navigate({ to: path as any, search: search as any })
                  },
        replace:  (url: string | { pathname?: string; query?: any }, as?: any, _opts?: any) => {
                      const { path, search } = splitHref(resolveHref(url, as))
                      // Guard 1: if the component's last render was on a different page
                      // than the current browser location, skip this replace.  This
                      // prevents stale mount-effects (e.g. ScTabFilter on the previous
                      // list page) from overwriting the destination page's search
                      // params — e.g. replacing /invoice/create?module=… with ?tab=all.
                      // pathnameAtRender is a captured primitive string (not a reference)
                      // so it reflects the page this hook instance was rendered on,
                      // even if TanStack Router mutates the location object in-place
                      // before the stale effect closure executes.
                      if (pathnameAtRender !== window.location.pathname) return Promise.resolve()
                      // Guard 2: if a navigation is currently in progress (TanStack Router's
                      // pending location differs from its resolved/committed location), this
                      // replace comes from a component that is remounting mid-transition due
                      // to route caching.  Guard 1 cannot catch this case because the
                      // component's first render already sees the NEW pathname (TanStack Router
                      // updates its location store synchronously before React commits).
                      // Allowing the replace would overwrite the destination's search params
                      // (e.g. stale ScTabFilter replacing ?tab=billed with ?tab=all).
                      const resolvedPathname = tsRouter.stores.resolvedLocation.get()?.pathname
                      if (resolvedPathname !== undefined && resolvedPathname !== location.pathname) return Promise.resolve()
                      return navigate({ to: path as any, search: search as any, replace: true })
                  },
        back:     () => window.history.back(),
        reload:   () => window.location.reload(),
        pathname: location.pathname,
        asPath:   location.href,
        query,
        isReady:  true,
        events:   routerEvents,
    }
}

// Singleton for imperative usage: import Router from 'next/router'; Router.push('/...')
// Some files also use Router.router.push() — expose self as .router
const Router = {
    events:  routerEvents,
    push:    (url: string | { pathname?: string; query?: any }, as?: any, _opts?: any) => {
                 const { path, search } = splitHref(resolveHref(url, as))
                 return tsRouter.navigate({ to: path as any, search: search as any })
             },
    replace: (url: string | { pathname?: string; query?: any }, as?: any, _opts?: any) => {
                 const { path, search } = splitHref(resolveHref(url, as))
                 return tsRouter.navigate({ to: path as any, search: search as any, replace: true })
             },
    back:    () => window.history.back(),
    reload:  () => window.location.reload(),
    get pathname() { return window.location.pathname },
    get asPath()   { return window.location.pathname + window.location.search + window.location.hash },
    get query()    { return Object.fromEntries(new URLSearchParams(window.location.search)) },
    get router(): any { return Router },
}

export default Router

// Expose the singleton on window so Playwright e2e tests can drive
// client-side navigation without going through a full page reload.
// Router only handles in-app navigation — no auth tokens or sensitive
// data are surfaced.
;(window as any).__compatRouter__ = Router
